import { WithConfirmationDialog, withConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { WithPromisedComponent, withPromisedComponent } from "@components/dialog/withPromisedComponent";
import { ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { IEntity } from "@odata/BindingContext";
import {
    DocumentEntity,
    IDocumentLinkEntity,
    IFileMetadataEntity,
    IInternalDocumentEntity,
    IInternalDocumentItemEntity,
    InternalDocumentEntity
} from "@odata/GeneratedEntityTypes";
import { DocumentLinkTypeCode, DocumentTypeCode } from "@odata/GeneratedEnums";
import { ORIGINAL_CLEARED_AMOUNT_PATH } from "@pages/admin/vatRules/VatRules.global.utils";
import { loadCategoriesMap } from "@pages/cashBasisAccounting/CashBasisAccounting.utils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import { isNotDefined } from "@utils/general";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";

import Dialog from "../../../components/dialog/Dialog";
import { WithDomManipulator, withDomManipulator } from "../../../contexts/domManipulator/withDomManipulator";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { PageViewMode } from "../../../enums";
import { StorageModel } from "../../../model/StorageModel";
import { IFormViewProps } from "../../../views/formView/FormView";
import { AccountAssignmentDialog } from "../../accountAssignment/AccountAssignmentDialog";
import { WithMinorAssetPairing } from "../../asset/minorAsset/withMinorAssetPairing";
import DialogPage from "../../DialogPage";
import { DocumentFormViewForExtend } from "../DocumentFormView";
import { IDocumentCustomData, IDocumentExtendedEntity } from "../DocumentInterfaces";
import { WithAccruals } from "../extensions/accruals/withAccruals";
import { WithTimeResolution } from "../extensions/timeResolution/withTimeResolution";
import { ADD_CLEARING_ITEM } from "./InternalDocumentDef";
import { getDefinitions } from "./InternalDocumentPairDef";
import InternalDocumentPairTableView from "./InternalDocumentPairTableView";

interface IInternalDocumentCustomData extends IDocumentCustomData {
    pairedDialogOpened?: boolean;
}

interface IInternalDocumentFormViewProps extends IFormViewProps<IDocumentExtendedEntity, IInternalDocumentCustomData>, WithConfirmationDialog,
    WithMinorAssetPairing, WithTimeResolution, WithAccruals, WithDomManipulator, WithPromisedComponent {
}

class InternalDocumentFormView extends DocumentFormViewForExtend<IInternalDocumentFormViewProps> {
    documentTypeCode = DocumentTypeCode.InternalDocument;

    async onAfterLoad(): Promise<void> {
        if (this.isCashBasisAccountingCompany) {
            const { storage } = this.props;
            loadCategoriesMap(storage);
            if (!storage.data.bindingContext.isNew()) {

                // while clearing by internal document on cash basis accounting company, internal document item is always in company
                // currency, but cleared amount is in currency of cleared document, we need it and only way to get that value is
                // from according cbaEntry or document link
                const clearingDocLinks = storage.data.entity.DocumentLinks
                        .filter((dl: IDocumentLinkEntity) => dl.TypeCode === DocumentLinkTypeCode.IDCL);

                for (const item of this.entity.Items) {
                    const linkedDocId = (item as IInternalDocumentItemEntity).LinkedDocument?.Id;
                    if (linkedDocId) {
                        const clearedAmount = clearingDocLinks.find(cdl => cdl.TargetDocument?.Id === linkedDocId).TransactionAmount;
                        item.TransactionAmount = clearedAmount;
                        const origItem = storage.data.origEntity.Items?.find(origItem => origItem.Id === item.Id);
                        if (origItem) {
                            origItem[ORIGINAL_CLEARED_AMOUNT_PATH] = clearedAmount;
                        }
                    }
                }
            }
        }
        return super.onAfterLoad();
    }

    handleFilesUploaded = async (_files: IFileMetadataEntity[]): Promise<void> => {
        // ISDOC extraction is not supported for internal documents
    };

    get datePropForNumberRange(): string {
        if (this.isCashBasisAccountingCompany) {
            return InternalDocumentEntity.DateCbaEntry;
        }
        return DocumentEntity.DateAccountingTransaction;
    }

    handleCustomLineItemAction = async (args: ISmartFastEntriesActionEvent): Promise<void> => {
        if (args.customActionType === ADD_CLEARING_ITEM) {
            const { storage } = this.props;
            const datePath = this.datePropForNumberRange;
            const dateBc = storage.data.bindingContext.navigate(datePath);
            const hasError = await storage.validateField(dateBc);
            if (!hasError) {
                this.props.storage.setCustomData({ pairedDialogOpened: true });
            }
            this.props.storage.refresh();
        }
    };

    handleClosePairing = () => {
        const { storage } = this.props;
        storage.setCustomData({ pairedDialogOpened: false });
        storage.clearEmptyLineItems(InternalDocumentEntity.Items);
        storage.refresh();
    };

    renderClearingDialog = (): ReactElement => {
        const storage = this.props.storage;

        if (!storage.getCustomData().pairedDialogOpened) {
            return null;
        }
        return <Dialog
            isEditableWindow
            onConfirm={null}
            onClose={this.handleClosePairing}>
            <DialogPage
                pageViewMode={PageViewMode.FormReadOnly}
                rootStorage={storage as StorageModel}
                tableView={InternalDocumentPairTableView}
                getDef={getDefinitions}/>
        </Dialog>;
    };

    documentSpecificChangeHandlers(e: ISmartFieldChange): void {
        super.documentSpecificChangeHandlers(e);
        if (e.bindingContext.getPath() === this.datePropForNumberRange && (e.triggerAdditionalTasks || e.value === null)) {
            this.props.storage.refreshGroupByKey("Items"); // refresh "Add clearing item" button
        }
    }

    documentSpecificBlurHandlers(e: ISmartFieldBlur): void {
        super.documentSpecificBlurHandlers(e);
        if (e.bindingContext.getPath() === this.datePropForNumberRange) {
            this.props.storage.refreshGroupByKey("Items"); // refresh "Add clearing item" button (after validation)
        }
    }

    onBeforeSave(data: IInternalDocumentEntity): IEntity {
        const entity = cloneDeep(data);

        // don't change DocumentLinks of other types, we just manage InternalDocumentClearing here
        const documentLinks = (entity.DocumentLinks ?? []).filter(link => link.TypeCode !== DocumentLinkTypeCode.IDCL);

        for (const item of entity.Items) {
            if (item.LinkedDocument?.Id) {
                const documentLink = entity.DocumentLinks?.find(link => link.TargetDocument?.Id === item.LinkedDocument.Id);

                const clearedAmount = item.TransactionAmount;
                if (documentLink) {
                    documentLinks.push({
                        ...documentLink,
                        TransactionAmount: clearedAmount
                    });
                } else {
                    documentLinks.push({
                        TypeCode: DocumentLinkTypeCode.IDCL,
                        TransactionAmount: clearedAmount,
                        TargetDocument: item.LinkedDocument
                    });
                }
            }

            item.AmountNet = item.Amount;
            if (isNotDefined(item.TransactionCurrencyCode) || item.TransactionCurrencyCode === getCompanyCurrency(this.props.storage.context)) {
                item.TransactionAmount = item.Amount;
            }
            item.TransactionAmountNet = item.TransactionAmount;

            if (isNotDefined(item.TransactionAmountVat)) {
                item.TransactionAmountVat = 0;
            }
        }

        entity.DocumentLinks = documentLinks;

        return entity;
    }

    render() {
        if (!this.isReady()) {
            return null;
        }

        return (
            <>
                {this.renderForm()}
                <AccountAssignmentDialog storage={this.props.storage}
                                         onChange={this.handleChange}
                                         onTemporalChange={this.handleTemporalChange}
                                         onConfirm={this.updateItemsVat}/>
                {this.renderCustomCbaCategoryDialog()}
                {this.renderVatDialog()}
                {this.renderClearingDialog()}
            </>
        );
    }
}

export default withPromisedComponent(withConfirmationDialog(withPermissionContext(withDomManipulator(InternalDocumentFormView))));
